export type OpenDeepLink = {
  path: string;
  hash?: string;
  search?: Record<string, string>;
  shouldNavigate: boolean;
};

type OpenDeepLinkIntent = Pick<OpenDeepLink, 'path' | 'hash'>;

function normalizeOpenPath(path: string) {
  return path || '/';
}

function getSearchPayload(searchParams: URLSearchParams) {
  const search = Object.fromEntries(searchParams.entries());
  return Object.keys(search).length > 0 ? search : undefined;
}

function parseOpenDeepLinkFallback(deepLink: string): OpenDeepLink | null {
  if (!deepLink.includes('/open')) {
    return null;
  }

  const pathMatch = deepLink.match(/\/open(.*)/);
  if (!pathMatch?.[1]) {
    return { path: '/', shouldNavigate: true };
  }

  const remainder = pathMatch[1];
  const [pathAndSearch = '', hash = ''] = remainder.split('#');
  const [path = '', searchString = ''] = pathAndSearch.split('?');

  return {
    path: normalizeOpenPath(path),
    hash: hash || undefined,
    search: searchString
      ? getSearchPayload(new URLSearchParams(searchString))
      : undefined,
    shouldNavigate: shouldNavigateForOpenDeepLink({
      path: normalizeOpenPath(path),
      hash: hash || undefined,
    }),
  };
}

export function parseOpenDeepLink(deepLink: string): OpenDeepLink | null {
  if (!deepLink.includes('/open')) {
    return null;
  }

  try {
    const url = new URL(deepLink);
    if (url.hostname !== 'open') {
      return null;
    }

    const path = normalizeOpenPath(url.pathname);
    const hash = url.hash ? url.hash.slice(1) : undefined;

    return {
      path,
      hash,
      search: getSearchPayload(url.searchParams),
      shouldNavigate: shouldNavigateForOpenDeepLink({ path, hash }),
    };
  } catch {
    return parseOpenDeepLinkFallback(deepLink);
  }
}

export function shouldNavigateForOpenDeepLink({
  path,
  hash,
}: OpenDeepLinkIntent): boolean {
  // A route-less deep link like ...://open/#settings/General means
  // "open UI on top of the current screen", not "navigate Home first"
  if (path === '/' && hash?.startsWith('settings/')) {
    return false;
  }

  return true;
}
